import { SimpleTableBase } from '@core/components/Table';
import { TableActionButtonDelete, TableActionButtonEdit } from '@core/components/TableAction';
import TableActionContainer from '@core/components/TableAction/TableActionContainer';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Tooltip } from '@material-ui/core';
import PartnerBankIcon from 'app/assets/icon/partnerBankIcon';
import { getCurrency } from 'app/utils/monetarioUtil';
import React, { useContext } from 'react';
import { ContaContext } from '../../ContaContext';
import ContaFab from '../ContaFab/ContaFab';
import { modaisConta } from '../modal/modais';
import { contaStyles } from './styles';

const ContaTable = ({ onSelecaoChange, onTipoSelecaoChange, fetchContas, fetchConta, onDelete }) => {
  const [contas] = useContext(ContaContext).contas;
  const [selecao] = useContext(ContaContext).selecao;
  const [loading] = useContext(ContaContext).loading;
  const [, setModal] = useContext(ContaContext).modal;
  const [totalElements] = useContext(ContaContext).totalElements;
  const [search] = useContext(ContaContext).search;
  const [tipoSelecao] = useContext(ContaContext).tipoSelecao;
  const [, setPage] = useContext(ContaContext).page;
  const [, setSize] = useContext(ContaContext).size;

  const styles = contaStyles();
  const handleOpenVisualizarModal = async (conta) => {
    await fetchConta(conta.id);

    switch (conta.tipo) {
      case 'CONTA_CORRENTE':
      case 'CONTA_POUPANCA':
      case 'CONTA_INVESTIMENTO':
        setModal(modaisConta.visualizarContaBancaria);
        break;
      case 'CONTA_CARTAO':
        setModal(modaisConta.visualizarContaCartao);
        break;
      case 'CONTA_CAIXINHA':
        setModal(modaisConta.visualizarContaCaixinha);
        break;
      default:
        break;
    }
  };

  const handleOpenEditarModal = async (conta) => {
    const abrirModal = () => {
      switch (conta.tipo) {
        case 'CONTA_CORRENTE':
        case 'CONTA_POUPANCA':
        case 'CONTA_INVESTIMENTO':
          setModal(modaisConta.editarContaBancaria);
          break;
        case 'CONTA_CARTAO':
          setModal(modaisConta.editarContaCartao);
          break;
        case 'CONTA_CAIXINHA':
          setModal(modaisConta.editarContaCaixinha);
          break;
        default:
          break;
      }
    };

    await fetchConta(conta.id);

    abrirModal();
  };

  const contaColumnDefs = [
    {
      title: 'Descrição',
      name: 'descricao',
      getCellValue: (conta) => {
        return (
          <div className={styles.descricaoContainer}>
            <span className={styles.descricao}>{conta?.descricao}</span>
            {conta?.partnerBank ? (
              <div className={styles.partnerBank}>
                <PartnerBankIcon size="20" />
              </div>
            ) : conta?.principal ? (
              <div className={styles.badgePrincipal}>Principal</div>
            ) : (
              ''
            )}
            {conta?.dataEncerramento ? <div className={styles.badgeEncerrada}>Encerrada</div> : ''}
          </div>
        );
      },
      sortingEnabled: true,
    },
    {
      title: 'Banco',
      name: 'contaBancaria.banco.nome',
      sortingEnabled: false,
      getCellValue: (conta) => {
        return conta?.banco?.nome || '-';
      },
    },
    {
      title: 'Tipo da conta',
      name: 'tipo',
      sortingEnabled: false,
      hideTooltip: true,
      getCellValue: (conta) => {
        if (conta.tipo) {
          switch (conta.tipo) {
            case 'CONTA_CORRENTE':
              return 'Corrente';
            case 'CONTA_POUPANCA':
              return 'Poupança';
            case 'CONTA_INVESTIMENTO':
              return 'Investimento';
            case 'CONTA_CAIXINHA':
              return 'Caixinha';
            case 'CONTA_CARTAO':
              return 'Cartão';
            default:
              return '-';
          }
        }

        return '-';
      },
    },
    {
      title: 'Saldo atual (R$)',
      name: 'saldoAtual',
      getCellValue: (conta) => {
        return (
          <Tooltip title="Saldo que considera contas baixadas no sistema até a data atual">
            <div className={conta?.saldoAtual < 0 ? styles.saldoNegativo : ''}>
              {conta?.saldoAtual !== null && conta?.saldoAtual !== undefined ? getCurrency(conta.saldoAtual) : '-'}
            </div>
          </Tooltip>
        );
      },
      sortingEnabled: false,
      hideTooltip: true,
      align: 'right',
    },
    {
      title: 'Saldo futuro (R$)',
      name: 'saldoFuturo',
      getCellValue: (conta) => {
        return (
          <Tooltip title="Saldo que considera contas baixadas no sistema com datas posteriores à atual">
            <div className={conta?.saldoFuturo < 0 ? styles.saldoNegativo : ''}>
              {conta?.saldoFuturo !== null && conta?.saldoFuturo !== undefined ? getCurrency(conta.saldoFuturo) : '-'}
            </div>
          </Tooltip>
        );
      },
      sortingEnabled: false,
      hideTooltip: true,
      align: 'right',
    },
    {
      name: '',
      title: '',
      sortingEnabled: false,
      align: 'left',
      width: 125,
      getCellValue: (conta) => (
        <TableActionContainer>
          {permissaoUtils.conta().editar && <TableActionButtonEdit onClick={() => handleOpenEditarModal(conta)} />}
          {permissaoUtils.conta().excluir && (
            <TableActionButtonDelete onClick={() => onDelete({ ids: [conta], q: search, tipoSelecao: 'NENHUM' })} />
          )}
        </TableActionContainer>
      ),
    },
  ];

  return (
    <>
      <SimpleTableBase
        columns={contaColumnDefs}
        rows={contas}
        fetchData={fetchContas}
        loading={loading}
        onRowClick={handleOpenVisualizarModal}
        defaultSorting={[{ columnName: 'descricao', direction: 'asc' }]}
        totalCount={totalElements}
        onCurrentPageChange={(params) => {
          setPage(params);
        }}
        onPageSizeChange={(params) => {
          setSize(params);
        }}
        onSelectionTypeChange={onTipoSelecaoChange}
        selectionType={tipoSelecao}
        fetchOnCreate
        pageType="async" // tem que passar isso para funcionar o fetchOnCreate. Só dizer que é para buscar os dados na inicialização não basta.
        selection={selecao}
        onSelectionChange={onSelecaoChange}
        onSortingChange={(params) => console.log(params)}
        onLoadingChange={(params) => console.log('loading change', params)}
        searchBy={search}
      />
      {(permissaoUtils.conta().cadastrar || permissaoUtils.conta().excluir) && (
        <ContaFab selected={selecao.length} onDelete={onDelete} fetchConta={fetchConta} />
      )}
    </>
  );
};

export default ContaTable;
